import { useState } from 'react';
import { T, useGT, Var } from 'gt-react';
import { ArrowsDownUpIcon } from '@phosphor-icons/react';
import type { UploadLayout } from '@erp/rpc';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FileDropzone } from '../../item-master/components/FileDropzone';
import { RESULTS_ACCEPT, validateResultsFile } from './resultsFile';
import {
  assignFiles,
  defaultSupplierId,
  lookupOf,
  supplierChoiceState,
} from './supplierChoice';

interface Props {
  /** The dropped files waiting for their supplier; empty closes the dialog. */
  files: File[];
  layouts: UploadLayout[];
  /**
   * False until the saved layouts have loaded: choosing before then could
   * pick a built-in layout a saved one is about to replace.
   */
  ready: boolean;
  /** `lookupFile` is the supplier's second file, when its layout reads one. */
  onConfirm: (
    layoutId: string,
    resultsFile: File,
    lookupFile: File | null,
  ) => void;
  onCancel: () => void;
}

/** Asks which supplier a dropped file is from before converting it. */
export function SupplierDialog({
  files,
  layouts,
  ready,
  onConfirm,
  onCancel,
}: Props) {
  const dropKey = files.map(({ name }) => name).join('|');
  return (
    <Dialog
      open={files.length > 0}
      onOpenChange={(open) => !open && onCancel()}
    >
      <DialogContent className="max-w-md">
        <SupplierChoice
          // Re-keyed per drop, and once the layouts load, so each drop starts
          // from a fresh choice and the single-supplier default is final.
          key={`${dropKey}:${String(ready)}`}
          files={files}
          layouts={layouts}
          ready={ready}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      </DialogContent>
    </Dialog>
  );
}

function SupplierChoice({ files, layouts, ready, onConfirm, onCancel }: Props) {
  const [layoutId, setLayoutId] = useState(() =>
    defaultSupplierId(layouts, ready),
  );
  // A second file dropped here belongs to the supplier chosen when it was
  // dropped; switching supplier must not hand it to another.
  const [droppedLookup, setDroppedLookup] = useState<{
    layoutId: string | null;
    file: File;
  } | null>(null);
  const [swapped, setSwapped] = useState(false);
  const lookup = lookupOf(layouts, layoutId);
  const assigned = assignFiles(files, lookup, swapped);
  // The second file came with the drop, or is dropped here.
  const droppedForThis =
    droppedLookup?.layoutId === layoutId ? droppedLookup.file : null;
  const lookupFile = assigned.lookup ?? droppedForThis;
  const { needsLookup, canConvert } = supplierChoiceState({
    layouts,
    layoutId,
    lookupFile,
    ready,
  });
  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <T context="title of the dialog asking which supplier dropped results files are from">
            Which supplier is this from?
          </T>
        </DialogTitle>
        <DialogDescription>
          <T context="explanation in the supplier dialog">
            The files are converted with that supplier’s layout.
          </T>
        </DialogDescription>
      </DialogHeader>
      <SupplierSelect
        layouts={layouts}
        value={layoutId}
        disabled={!ready}
        onChange={setLayoutId}
      />
      <DroppedFiles
        results={assigned.results}
        lookup={
          needsLookup && assigned.lookup
            ? { label: needsLookup.label, file: assigned.lookup }
            : null
        }
        onSwap={() => setSwapped((current) => !current)}
      />
      {needsLookup && !assigned.lookup ? (
        <LookupFileDrop
          label={needsLookup.label}
          file={droppedForThis}
          onFile={(file) => setDroppedLookup({ layoutId, file })}
        />
      ) : null}
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>
          <T context="button that cancels converting dropped files">Cancel</T>
        </Button>
        <Button
          disabled={!canConvert || !assigned.results}
          onClick={() => {
            if (layoutId && assigned.results) {
              onConfirm(
                layoutId,
                assigned.results,
                needsLookup ? lookupFile : null,
              );
            }
          }}
        >
          <T context="button that converts the dropped files with the chosen supplier">
            Convert
          </T>
        </Button>
      </DialogFooter>
    </>
  );
}

/** Which dropped file is which, with a swap for a wrong guess. */
function DroppedFiles({
  results,
  lookup,
  onSwap,
}: {
  results: File | null;
  lookup: { label: string; file: File } | null;
  onSwap: () => void;
}) {
  if (!results) return null;
  return (
    <div className="flex flex-col gap-1 text-sm">
      <div className="truncate">
        <T context="supplier dialog: which dropped file is the results workbook">
          Results: <Var>{results.name}</Var>
        </T>
      </div>
      {lookup ? (
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate">
            <T context="supplier dialog: which dropped file is the supplier's second file; the first variable is its label, e.g. Feedback">
              <Var>{lookup.label}</Var>: <Var>{lookup.file.name}</Var>
            </T>
          </span>
          <Button size="sm" variant="ghost" onClick={onSwap}>
            <ArrowsDownUpIcon className="h-4 w-4" />
            <T context="button that swaps which dropped file is the results and which the second file">
              Swap
            </T>
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Where a supplier's second file (e.g. Stelpro's Feedback) is dropped. */
function LookupFileDrop({
  label,
  file,
  onFile,
}: {
  label: string;
  file: File | null;
  onFile: (file: File) => void;
}) {
  const gt = useGT();
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm">
        <T context="label over the second-file drop area in the supplier dialog; the variable is the file's name, e.g. Feedback">
          This supplier also needs its <Var>{label}</Var> file:
        </T>
      </span>
      <FileDropzone
        className="px-3 py-3"
        accept={RESULTS_ACCEPT}
        prompt={
          file ? (
            <Var>{file.name}</Var>
          ) : (
            <T context="instruction inside the second-file drop area">
              Drop the .xlsx here.
            </T>
          )
        }
        validate={(dropped) =>
          Promise.resolve(validateResultsFile(dropped, gt))
        }
        onFileAccepted={onFile}
      />
    </div>
  );
}

function SupplierSelect({
  layouts,
  value,
  disabled,
  onChange,
}: {
  layouts: UploadLayout[];
  value: string | null;
  disabled: boolean;
  onChange: (layoutId: string) => void;
}) {
  const gt = useGT();
  return (
    <Select
      value={value ?? ''}
      onValueChange={(next) => {
        if (typeof next === 'string' && next !== '') onChange(next);
      }}
    >
      <SelectTrigger className="w-full" disabled={disabled}>
        <SelectValue
          placeholder={gt('Choose a supplier', {
            context:
              'placeholder of the supplier dropdown in the upload dialog',
          })}
        />
      </SelectTrigger>
      <SelectContent>
        {layouts.map((layout) => (
          <SelectItem key={layout.id} value={layout.id}>
            {layout.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
